import React, { useCallback } from 'react';
import { useLingui } from '@lingui/react/macro';

import { LOCATION_DEFAULT_PLACEHOLDER } from '@rover/constants/src/location/location.constants';
import { Box, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import LocationInputContainer from '@rover/react-lib/src/containers/LocationInputContainer';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import { ColorfulPerfectMatchFieldInteraction } from './analyticsEvents';

type LocationChangeData = {
  value?: string | null;
  lat?: string | null;
  lng?: string | null;
  locationAccuracy?: string | null;
  locationType?: string | null;
  rawLocationTypes?: string[] | null;
};

type Props = {
  location?: string;
  onChange: (filters: Partial<SearchFilters>) => void;
};

const LocationInput = ({ location, onChange }: Props): JSX.Element => {
  const theme = useTheme();
  const { t, i18n } = useLingui();

  const handleChange = useCallback(
    (data: LocationChangeData | null): void => {
      emitAnalyticsEvent(new ColorfulPerfectMatchFieldInteraction({ field: 'location' }));
      onChange({
        centerlat: data?.lat ? parseFloat(data.lat) : undefined,
        centerlng: data?.lng ? parseFloat(data.lng) : undefined,
        location: data?.value || undefined,
        locationAccuracy: data?.locationAccuracy || undefined,
        locationType: data?.locationType || undefined,
        maxlat: undefined,
        maxlng: undefined,
        minlat: undefined,
        minlng: undefined,
        rawLocationTypes: data?.rawLocationTypes || undefined,
        zoomlevel: undefined,
      });
    },
    [onChange]
  );

  return (
    <Box>
      <Text size="200" fontWeight="semibold" mb="1x" display="block">
        {t`What's your address?`}
      </Text>
      <LocationInputContainer
        channel="ssr-search"
        enableLatLng
        inputValue={location}
        onChange={handleChange}
        placeholder={i18n._(LOCATION_DEFAULT_PLACEHOLDER)}
        placeholderColor={theme.textColor.placeholder}
        validationType="inline"
        enableTwoLineSuggestions
      />
    </Box>
  );
};

export default LocationInput;
